// Texts of the review sentiment analysis tool. The English version is written
// for US search (target keywords: "customer review sentiment analysis",
// "review sentiment analysis"), not translated word for word.
//
// Theme ids are shared by both languages (the back and the merge logic key on
// them); only the names change. The name of the current language is sent to
// the back, which gives it to the AI.
const content = {
  fr: {
    seo: {
      title: "Analyse sémantique des avis clients | Outil gratuit | Localranker",
      description:
        "Analysez gratuitement le sentiment et les thématiques clés de vos avis clients Google. Identifiez vos forces et axes d'amélioration grâce à l'IA.",
    },
    hero: {
      title: "Analyse sémantique des avis clients",
      subtitle:
        "Importez vos avis, choisissez vos thématiques et obtenez un rapport d'analyse avec score par thème, SWOT et recommandations.",
      cta: "Analyser mes avis",
    },
    themes: {
      accueil: "Accueil",
      service_client: "Service client",
      expertise: "Expertise / Compétence",
      reactivite: "Réactivité",
      qualite_produits: "Qualité des produits",
      variete_choix: "Variété / Choix",
      rapport_qualite_prix: "Rapport qualité-prix",
      proprete: "Propreté / Hygiène",
      ambiance: "Ambiance / Cadre",
      accessibilite: "Accessibilité / Parking",
      confort: "Confort",
      temps_attente: "Temps d'attente",
      horaires: "Horaires",
      livraison: "Livraison",
      disponibilite: "Disponibilité",
      communication: "Communication",
      sav: "SAV / Après-vente",
      fidelisation: "Fidélisation",
      recommandation: "Recommandation",
      reservation: "Réservation en ligne",
      transparence: "Transparence",
      fiabilite: "Fiabilité",
      securite: "Sécurité",
      site_web: "Site web / App",
    },
    sentiments: {
      positive: "Positif",
      negative: "Négatif",
      neutral: "Neutre",
      mixed: "Mitigé",
    },
    csvTemplate:
      'review_content,rating,review_date\n"Super accueil, personnel très agréable.",5,2026-03-01\n"Attente trop longue au comptoir.",2,2026-02-15\n"Bon rapport qualité/prix.",4,\n',
    toasts: {
      maxThemes: (max) => `Maximum ${max} thématiques.`,
      badFormat: "Format non supporté. Utilisez un fichier .csv",
      noReviewsInCsv:
        'Aucun avis trouvé. Vérifiez que la première colonne est "review_content".',
      noReviews: "Ajoutez au moins un avis à analyser.",
      noThemes: "Sélectionnez au moins une thématique.",
      captcha: "Veuillez résoudre le captcha.",
      error: "Une erreur est survenue.",
      network: "Impossible de contacter le serveur. Réessayez.",
    },
    form: {
      step1: "Votre établissement",
      placePlaceholder: "Recherchez votre établissement...",
      step2: "Importez vos avis",
      tabPaste: "Coller des avis",
      tabCsv: "Importer un CSV",
      textareaPlaceholder:
        "Collez vos avis ici, un par paragraphe (séparez chaque avis par une ligne vide)...",
      dropHere: "Déposez votre fichier CSV ici",
      dropHint: "Ou glissez un fichier CSV.",
      downloadTemplate: "Télécharger le modèle CSV",
      step3: "Thématiques",
      reviewCount: (n) => `${n} avis`,
      submit: "Lancer l'analyse",
      submitting: "Analyse en cours...",
    },
    loading: (n) => `Analyse de ${n} avis en cours...`,
    results: {
      reportFor: (name) => `Rapport — ${name}`,
      report: "Rapport d'analyse",
      analyzed: (n) => `${n} avis analysés`,
      avgRating: "Note moyenne",
      byTheme: "Analyse par thématique",
      detected: "Thématiques détectées par l'IA",
      swot: "Analyse SWOT",
      strengths: "Forces",
      weaknesses: "Faiblesses",
      opportunities: "Opportunités",
      threats: "Menaces",
      recommendations: "Recommandations",
    },
    demo: {
      title: "Analysez tous vos avis automatiquement",
      desc: "Analysez en continu le sentiment de vos avis Google sur l'ensemble de vos établissements et pilotez votre e-réputation.",
      cta: "Demander une démo",
    },
    seoSection: {
      whyTitle: "Pourquoi analyser le sentiment de vos avis clients ?",
      whyText:
        "Les avis clients sont une mine d'informations. Au-delà de la note moyenne, le contenu textuel de chaque avis révèle ce que vos clients apprécient, ce qui les déçoit, et les thématiques qui reviennent le plus souvent. L'analyse sémantique transforme ces retours en insights actionnables.",
      howTitle: "Comment fonctionne l'analyse ?",
      howText:
        "Notre outil utilise l'intelligence artificielle pour analyser vos avis en profondeur.",
      steps: [
        {
          title: "Importez vos avis",
          text: "Collez vos avis ou importez un fichier CSV. Jusqu'à 500 avis par analyse.",
        },
        {
          title: "Choisissez vos thématiques",
          text: "Sélectionnez jusqu'à 5 thématiques parmi 24 critères : accueil, qualité, prix, délais...",
        },
        {
          title: "Recevez votre rapport",
          text: "Score par thématique, analyse SWOT, verbatims clés et recommandations en quelques secondes.",
        },
      ],
      reputationTitle: "Comment l'analyse sémantique améliore votre e-réputation ?",
      reputationText:
        "En identifiant précisément les points de friction et les points forts perçus par vos clients, vous pouvez prioriser vos actions d'amélioration, former vos équipes et mettre en avant vos atouts. L'analyse SWOT transforme vos avis en plan d'action concret.",
    },
    modal: {
      title: "Encore une petite étape",
      submit: "Lancer l'analyse",
    },
  },
  en: {
    seo: {
      title: "Free Review Sentiment Analysis Tool | Localranker",
      description:
        "Run a free customer review sentiment analysis with AI. Score reviews by theme and get a SWOT report and clear recommendations for every location.",
    },
    hero: {
      title: "Customer Review Sentiment Analysis",
      subtitle:
        "Paste or upload your reviews, pick the themes that matter to you, and get a report with a score per theme, a SWOT analysis and recommendations.",
      cta: "Analyze my reviews",
    },
    themes: {
      accueil: "Greeting / Welcome",
      service_client: "Customer service",
      expertise: "Expertise / Know-how",
      reactivite: "Responsiveness",
      qualite_produits: "Product quality",
      variete_choix: "Selection / Variety",
      rapport_qualite_prix: "Value for money",
      proprete: "Cleanliness / Hygiene",
      ambiance: "Atmosphere / Setting",
      accessibilite: "Accessibility / Parking",
      confort: "Comfort",
      temps_attente: "Wait time",
      horaires: "Business hours",
      livraison: "Delivery",
      disponibilite: "Availability",
      communication: "Communication",
      sav: "After-sales service",
      fidelisation: "Customer loyalty",
      recommandation: "Likelihood to recommend",
      reservation: "Online booking",
      transparence: "Transparency",
      fiabilite: "Reliability",
      securite: "Safety / Security",
      site_web: "Website / App",
    },
    sentiments: {
      positive: "Positive",
      negative: "Negative",
      neutral: "Neutral",
      mixed: "Mixed",
    },
    csvTemplate:
      'review_content,rating,review_date\n"Friendly staff, quick and easy checkout.",5,2026-03-01\n"Waited way too long at the counter.",2,2026-02-15\n"Good value for the price.",4,\n',
    toasts: {
      maxThemes: (max) => `You can pick up to ${max} themes.`,
      badFormat: "Unsupported format. Please use a .csv file",
      noReviewsInCsv:
        'No reviews found. Make sure the first column is "review_content".',
      noReviews: "Add at least one review to analyze.",
      noThemes: "Select at least one theme.",
      captcha: "Please complete the captcha.",
      error: "Something went wrong.",
      network: "We couldn't reach the server. Please try again.",
    },
    form: {
      step1: "Your business",
      placePlaceholder: "Search for your business...",
      step2: "Add your reviews",
      tabPaste: "Paste reviews",
      tabCsv: "Upload a CSV",
      textareaPlaceholder:
        "Paste your reviews here, one per paragraph (leave a blank line between reviews)...",
      dropHere: "Drop your CSV file here",
      dropHint: "Or drag and drop a CSV file.",
      downloadTemplate: "Download the CSV template",
      step3: "Themes",
      reviewCount: (n) => `${n} ${n === 1 ? "review" : "reviews"}`,
      submit: "Run the analysis",
      submitting: "Analyzing...",
    },
    loading: (n) => `Analyzing ${n} ${n === 1 ? "review" : "reviews"}...`,
    results: {
      reportFor: (name) => `Report — ${name}`,
      report: "Analysis report",
      analyzed: (n) => `${n} ${n === 1 ? "review" : "reviews"} analyzed`,
      avgRating: "Average rating",
      byTheme: "Sentiment by theme",
      detected: "Themes detected by AI",
      swot: "SWOT analysis",
      strengths: "Strengths",
      weaknesses: "Weaknesses",
      opportunities: "Opportunities",
      threats: "Threats",
      recommendations: "Recommendations",
    },
    demo: {
      title: "Analyze all your reviews automatically",
      desc: "Track review sentiment across every location, all the time, and stay on top of your online reputation.",
      cta: "Book a demo",
    },
    seoSection: {
      whyTitle: "Why run a sentiment analysis on your customer reviews?",
      whyText:
        "Your customer reviews are full of insight. Beyond the star rating, the text of each review tells you what customers love, what lets them down, and which topics keep coming up. Review sentiment analysis turns that feedback into insights your teams can act on.",
      howTitle: "How does the review sentiment analysis work?",
      howText: "Our tool uses AI to read and analyze your reviews in depth.",
      steps: [
        {
          title: "Add your reviews",
          text: "Paste your reviews or upload a CSV file. Up to 500 reviews per analysis.",
        },
        {
          title: "Pick your themes",
          text: "Choose up to 5 themes out of 24: customer service, quality, price, wait time...",
        },
        {
          title: "Get your report",
          text: "A score per theme, a SWOT analysis, key quotes and recommendations, in seconds.",
        },
      ],
      reputationTitle: "How does review sentiment analysis improve your online reputation?",
      reputationText:
        "When you know exactly what customers praise and where they run into friction, you can prioritize fixes, coach your teams and showcase your strengths. For multi-location brands, it shows which topics need attention across the network. The SWOT analysis turns your reviews into a concrete action plan.",
    },
    modal: {
      title: "One more step",
      submit: "Run the analysis",
    },
  },
};

export default content;
